import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { supabase } from '../lib/supabase';

export interface FinanceUser {
  id: number;
  created_at: string;
  auth0ID: string;
  email: string;
  firstname: string;
  lastname: string;
  mobilenumber: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
}

export function useFinanceStatus() {
  const { user, isAuthenticated } = useAuth0();
  const [financeStatus, setFinanceStatus] = useState<'LOADING' | 'NOT_FOUND' | 'PENDING' | 'APPROVED' | 'DENIED'>('LOADING');
  const [financeUser, setFinanceUser] = useState<FinanceUser | null>(null);
  const [pendingFinanceUsers, setPendingFinanceUsers] = useState<FinanceUser[]>([]);
  const [allFinanceUsers, setAllFinanceUsers] = useState<FinanceUser[]>([]);
  const [loading, setLoading] = useState(true);

  const checkFinanceStatus = async () => {
    if (!user?.sub || !isAuthenticated) {
      setFinanceStatus('NOT_FOUND');
      setLoading(false);
      return;
    }

    try {
      const { data: financeUser, error } = await supabase
        .from('finance_users')
        .select('*')
        .eq('auth0id', user.sub)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking finance status:', error);
        setFinanceStatus('NOT_FOUND');
      } else if (financeUser) {
        setFinanceUser(financeUser);
        setFinanceStatus(financeUser.status);
      } else {
        setFinanceStatus('NOT_FOUND');
      }
    } catch (error) {
      console.error('Error checking finance status:', error);
      setFinanceStatus('NOT_FOUND');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingFinanceUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('finance_users')
        .select('*')
        .eq('status', 'PENDING')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending finance users:', error);
        return;
      }

      setPendingFinanceUsers(data || []);
    } catch (error) {
      console.error('Error fetching pending finance users:', error);
    }
  };

  const fetchAllFinanceUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('finance_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching all finance users:', error);
        return;
      }

      setAllFinanceUsers(data || []);
    } catch (error) {
      console.error('Error fetching all finance users:', error);
    }
  };

  const registerFinanceUser = async (userData: { 
    email: string; 
    firstname?: string; 
    lastname?: string; 
    mobilenumber: string; 
  }) => {
    if (!user?.sub) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('finance_users')
      .insert({
        auth0id: user.sub,
        email: userData.email,
        firstname: userData.firstname,
        lastname: userData.lastname,
        mobilenumber: userData.mobilenumber,
        status: 'PENDING'
      });

    if (error) throw error;

    await checkFinanceStatus();
  };

  const updateFinanceUserStatus = async (financeUserId: number, status: 'APPROVED' | 'DENIED') => {
    const { error } = await supabase
      .from('finance_users')
      .update({ status })
      .eq('id', financeUserId);

    if (error) throw error;

    await fetchPendingFinanceUsers();
    await fetchAllFinanceUsers();
  };

  const refreshStatus = async () => {
    await checkFinanceStatus();
  };

  const refreshPendingFinanceUsers = async () => {
    await fetchPendingFinanceUsers();
  };

  const refreshAllFinanceUsers = async () => {
    await fetchAllFinanceUsers();
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkFinanceStatus();
    }
  }, [isAuthenticated, user]);

  return {
    financeStatus,
    financeUser,
    pendingFinanceUsers,
    allFinanceUsers,
    loading,
    registerFinanceUser,
    updateFinanceUserStatus,
    refreshStatus,
    refreshPendingFinanceUsers,
    refreshAllFinanceUsers
  };
}
